import React, { useCallback, useEffect } from 'react';
import { graphql, useFragment } from 'react-relay';
import { Box, xcss } from '@atlaskit/primitives';
import type { groupTableBody_nativeIssueTable_groupResults$key } from '@atlassian/jira-relay/src/__generated__/groupTableBody_nativeIssueTable_groupResults.graphql.ts';
import type { groupTableBody_nativeIssueTable_project$key } from '@atlassian/jira-relay/src/__generated__/groupTableBody_nativeIssueTable_project.graphql.ts';
import type { Column, TableInjectedCellProps } from '../../../common/types.tsx';
import { getFlatListKey } from '../../../common/utils/get-flat-list-key/index.tsx';
import { useIsVirtualizationEnabled } from '../../../controllers/features/selectors.tsx';
import { useHierarchyActions } from '../../../controllers/hierarchy/index.tsx';
import { FirstGroupRow, GroupRow } from '../../row/group-row/index.tsx';
import { ChildRows } from '../../rows/child-rows/index.tsx';
import { RowContextProvider, type RenderChildRows } from '../../rows/row-context/index.tsx';
import { VirtualizedRows } from '../../rows/virtualized-rows-hierarchy/index.tsx';

export type Props = {
	groupResults: groupTableBody_nativeIssueTable_groupResults$key;
	columns: Column[];
	loading: boolean;
	tableInjectedProps: TableInjectedCellProps;
	project: groupTableBody_nativeIssueTable_project$key | null;
};

export const GroupTableBody = ({
	groupResults,
	project,
	columns,
	loading,
	...tableBodyProps
}: Props) => {
	const groupsData = useFragment<groupTableBody_nativeIssueTable_groupResults$key>(
		graphql`
			fragment groupTableBody_nativeIssueTable_groupResults on JiraSpreadsheetGroupConnection
			@argumentDefinitions(
				isInlineEditingEnabled: { type: "Boolean", defaultValue: true }
				isDensityFull: { type: "Boolean", defaultValue: false }
				isPaginating: { type: "Boolean!" }
				projectKeys: { type: "[String!]" }
				projectKey: { type: "String" }
			) {
				__id
				firstGroup @skip(if: $isPaginating) {
					id
					issues(fieldSetsInput: $fieldSetsInput, first: 50) {
						# eslint-disable-next-line @atlassian/relay/unused-fields
						edges {
							__typename
						}
					}
					...groupRow_nativeIssueTable_FirstGroupRow_firstGroupRow
						@arguments(
							isInlineEditingEnabled: $isInlineEditingEnabled
							isDensityFull: $isDensityFull
							projectKeys: $projectKeys
							projectKey: $projectKey
						)
				}
				edges {
					node {
						id
						...groupRow_nativeIssueTable_groupRow
					}
				}
			}
		`,
		groupResults,
	);

	const projectData = useFragment(
		graphql`
			fragment groupTableBody_nativeIssueTable_project on JiraProject {
				key
			}
		`,
		project,
	);

	const groupConnectionId = groupsData.__id;
	const { populateConnection } = useHierarchyActions();
	useEffect(() => {
		populateConnection({
			parentItemId: 'root',
			itemsConnectionId: groupConnectionId,
			parentConnectionId: groupConnectionId,
			list:
				groupsData?.edges
					// groupId for each group is the same as itemId
					?.map((edge) => edge?.node && { itemId: edge.node.id, key: null, groupId: edge.node.id })
					.filter(Boolean) ?? [],
		});
	}, [groupConnectionId, groupsData.edges, populateConnection]);

	useEffect(() => {
		return () => {
			populateConnection({
				parentItemId: 'root',
				itemsConnectionId: groupConnectionId,
				parentConnectionId: groupConnectionId,
				list: null,
			});
		};
	}, [groupConnectionId, populateConnection]);

	const renderChildRows: RenderChildRows = useCallback(
		(props) => <ChildRows projectKey={projectData?.key} isGroupingSupported {...props} />,
		[projectData?.key],
	);

	// Ignore firstGroup as a separate entity if it has no issues
	const firstGroup = groupsData?.firstGroup?.issues ? groupsData?.firstGroup : null;

	const renderGroups = useCallback(
		(isItemVirtual?: (itemId: string) => boolean) => {
			return (
				<>
					{firstGroup && (
						<RowContextProvider
							key={firstGroup.id}
							groupId={firstGroup.id}
							{...tableBodyProps}
							columns={columns}
							renderChildRows={renderChildRows}
						>
							<FirstGroupRow
								firstGroupRow={firstGroup}
								groupConnectionId={groupConnectionId}
								loading={loading}
								isVirtual={
									isItemVirtual?.(
										getFlatListKey({ itemId: firstGroup.id, groupId: firstGroup.id }),
									) ?? true
								}
							/>
						</RowContextProvider>
					)}
					{groupsData?.edges?.map(
						(group) =>
							group?.node &&
							group.node.id !== firstGroup?.id && (
								<RowContextProvider
									key={group.node.id}
									groupId={group.node.id}
									{...tableBodyProps}
									columns={columns}
									renderChildRows={renderChildRows}
								>
									<GroupRow
										groupRow={group.node}
										groupConnectionId={groupConnectionId}
										isVirtual={
											isItemVirtual?.(
												getFlatListKey({ itemId: group.node.id, groupId: group.node.id }),
											) ?? true
										}
									/>
								</RowContextProvider>
							),
					)}
				</>
			);
		},
		[
			columns,
			firstGroup,
			groupConnectionId,
			groupsData?.edges,
			loading,
			renderChildRows,
			tableBodyProps,
		],
	);

	const isVirtualizationEnabled = useIsVirtualizationEnabled();

	return (
		<Box as="tbody" xcss={tableBodyStyles}>
			{isVirtualizationEnabled ? (
				<VirtualizedRows hasSpacers columnsCount={columns.length}>
					{renderGroups}
				</VirtualizedRows>
			) : (
				renderGroups()
			)}
		</Box>
	);
};

const tableBodyStyles = xcss({
	borderBottom: 'none',
	overflowX: 'hidden',
});
